/*
 *
 * Copyright (C) 2022 NetBase Solutions, Inc.
 * 3960 Freedom Circle Suite 201 Santa Clara, CA 95054
 * All rights reserved.
 */

import React from 'react'
import { useTranslation } from 'react-i18next'
import { WidgetGuard } from 'features/types'
import { useDashboardWidgetSetting } from '../../../../../hooks/useDashboardState'
import { useDashboardWidgetData } from '../../../../../hooks/useWidgetState'
import ErrorMessage from '../../../../share/ErrorMessage'
import { WordCloudWidgetsType } from '../../types'
import { InsightCount } from '../../../../../schemas/action/InsightCountSchema'

const WordCloudBelowThresholdGuard: WidgetGuard = ({ widgetId, children }) => {
  const widgetSetting = useDashboardWidgetSetting<WordCloudWidgetsType>(widgetId)
  const widgetData = useDashboardWidgetData<InsightCount>(widgetId)
  const { t } = useTranslation()

  // NI-2615: defence-in-depth — `WordCloudEmptyResultGuard` upstream catches the
  // common malformed-shape cases (missing/empty `insights`, missing `dataset`),
  // but a thundering-herd partial response that slips through could hit this
  // guard with an undefined chain and re-throw `Cannot read properties of
  // undefined (reading '0')` to the outer ErrorBoundary. Optional-chain to []
  // and only show "below threshold" when there are actually values to evaluate
  // — `Array.every` on an empty array is vacuously true and would otherwise
  // mis-claim "emptyBecauseLowCount" for a missing-data case.
  const dataset = widgetData?.data?.[0]?.results?.[0]?.data?.insights?.[0]?.dataset ?? []
  const values = dataset.flatMap((entry) => entry.set ?? []).map((entry) => entry?.value ?? 0)

  if ('minCount' in widgetSetting && values.length > 0) {
    const isBelowThreshold = values.every((value) => value < (widgetSetting.minCount || 0))

    if (isBelowThreshold) return <ErrorMessage>{t('emptyBecauseLowCount')}</ErrorMessage>
  }

  return children
}

export default WordCloudBelowThresholdGuard
